import { BLUESKY_ENGAGEMENT_BENCHMARK } from "@/lib/bluesky-engagement-benchmarks";

/**
 * The FAQs for `/tools/bluesky-engagement-rate`, in their own module.
 *
 * They live apart from the tool component on purpose. A route's `head` is never
 * code-split (only `component` and `loader` are), so importing this list from
 * the component module would pull the whole component into the shared entry
 * chunk that every visitor to every page downloads — the trap recorded in
 * `marketing-perf-ceiling`, and one this repo has already been caught by once.
 *
 * Rendered visibly on the page AND emitted as `FAQPage` JSON-LD from the route.
 * Marking up a question that is not on the page is a structured-data violation,
 * so both read from this one list.
 */
const BANDS = BLUESKY_ENGAGEMENT_BENCHMARK.bands;
const SMALLEST = BANDS[0];
const LARGEST = BANDS[BANDS.length - 1];

const GOOD_RATE_ANSWER =
  SMALLEST && LARGEST
    ? `It depends on account size, because the rate falls as an audience grows. We measured ${BLUESKY_ENGAGEMENT_BENCHMARK.accountsMeasured.toLocaleString("en-US")} active Bluesky accounts: the typical median post earned ${SMALLEST.median}% for accounts with ${SMALLEST.label} followers and ${LARGEST.median}% for ${LARGEST.label}. Landing in your size band's middle half is normal. The most useful comparison is still against your own past: run this again in a month and see which way it moved.`
    : "";

export const ENGAGEMENT_FAQS = [
  {
    q: "How is Bluesky engagement rate calculated?",
    a: "Likes, reposts and replies on a post, divided by the account's follower count, expressed as a percentage. This page computes that for every one of your recent posts and reports the median — the typical post — rather than the average, which one unusually successful post can lift for good.",
  },
  {
    q: "What is a good engagement rate on Bluesky?",
    a: GOOD_RATE_ANSWER,
  },
  {
    q: "Why does this show a median instead of an average?",
    a: "Because an average is the wrong summary of a skewed distribution. If one post reaches far beyond your followers, it raises your average permanently, and every future post looks like a decline against a number a single post created. The median describes what a typical post actually does. Both are shown, and when they disagree by more than half again, the page says which posts are carrying the difference.",
  },
  {
    q: "Does this need my Bluesky password?",
    a: "No — and it never will. Everything here comes from Bluesky's public API, which needs no authentication, and the request is made by your own browser rather than by our servers. There is no login, no OAuth and no app password. Nothing about your account is stored.",
  },
  {
    q: "Why are my most recent posts not included?",
    a: "Posts less than 48 hours old are excluded because they are still collecting engagement. Counting them would mix in how recent a post is with how well it did, and would drag the median down for anyone who posts often. Only posts old enough to have settled are measured.",
  },
] as const;
